import Add from '@mui/icons-material/Add';
import EditOutlined from '@mui/icons-material/EditOutlined';
import InfoOutlined from '@mui/icons-material/InfoOutlined';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import { Theme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { useQueryClient } from '@tanstack/react-query';
import { BillingLineSide } from '@treadinc/horizon-api-spec';
import { t } from 'i18next';
import { useContext, useState } from 'react';

import PrevailingWageBadge from '~components/Common/PrevailingWageBadge';
import {
  OverflowAwareText,
  SmallButton,
} from '~components/Order/ordersDispatchStyledComponents';
import { Skeleton } from '~components/Skeleton/Skeleton';
import { useCompanyCurrency } from '~hooks/useCompanyCurrency/useCompanyCurrency';
import { Invoice } from '~hooks/useInvoices/models';
import { JobInvoicing } from '~hooks/useJob/models';
import useJobInvoicesQuery, { jobInvoicesQueryKey } from '~queries/useJobInvoicesQuery';
import { useStores } from '~store';
import { getEffectiveUserCompanyId } from '~utils/user/user-utils';

import { formatChargeRate } from '../chargeRateFormatting';
import ChargesBillingLinesQueryContext from '../ChargesBillingLinesQueryContext';
import {
  BILLING_LINE_SIDES,
  getBillingLinesParties,
  getLinesOfSide,
  sideLabelKeys,
} from '../chargeSelection';
import {
  chargesNestedHeaderTextSx,
  chargesRaisedCardSx,
  NO_VALUE_PLACEHOLDER,
  rejectedAmountSx,
} from '../chargesGrid';
import {
  ChargeRow,
  getTotalWithTax,
  isSideBilledPerLoad,
  JobCharges,
} from '../groupBillingLines';
import { getInvoiceRateCardName } from '../invoiceRateCard';
import ChargesInvoiceEditDialog from './ChargesInvoiceEditDialog';
import RejectedChip from './RejectedChip';
import SettlementNumberChip from './SettlementNumberChip';
import WasText from './WasText';

const PANEL_GRID_SX = {
  alignItems: 'baseline',
  display: 'grid',
  gap: 1,
  gridTemplateColumns:
    'minmax(0, 1.6fr) minmax(0, 0.6fr) minmax(0, 1fr) minmax(0, 0.9fr)',
} as const;

const sideDotColor = {
  [BillingLineSide.PAYABLE]: (theme: Theme) => theme.brandV2.colors.treadGreen,
  [BillingLineSide.RECEIVABLE]: (theme: Theme) => theme.brandV2.colors.treadRed,
} satisfies Record<BillingLineSide, (theme: Theme) => string>;

const noLinesKeys = {
  [BillingLineSide.PAYABLE]: 'approvals.approve_charges.side_panels.no_lines.payable',
  [BillingLineSide.RECEIVABLE]:
    'approvals.approve_charges.side_panels.no_lines.receivable',
} satisfies Record<BillingLineSide, string>;

const secondaryTextSx = (theme: Theme) => ({
  color: theme.brandV2.colors.treadGray2,
  fontSize: '12px',
});

interface PanelRate {
  isPercentRate?: boolean;
  rate: number | null;
  rateType: string | null;
}

const formatPanelRate = (
  { isPercentRate = false, rate, rateType }: PanelRate,
  currencyFormatter: (value: number | string) => string,
  unitStyle: 'full' | 'short' = 'short',
) =>
  rate === null
    ? NO_VALUE_PLACEHOLDER
    : formatChargeRate({ isPercentRate, rate, rateType }, currencyFormatter, unitStyle);

interface ChargesSidePanelsProps {
  job: JobInvoicing;
}

export default function ChargesSidePanels({ job }: ChargesSidePanelsProps) {
  const { chargesByJobId, reload } = useContext(ChargesBillingLinesQueryContext);
  const { userStore } = useStores();
  const companyId = getEffectiveUserCompanyId(userStore);
  const queryClient = useQueryClient();
  const [editedSide, setEditedSide] = useState<BillingLineSide | null>(null);
  const {
    isLoading: isInvoicesLoading,
    payable,
    receivable,
  } = useJobInvoicesQuery(companyId, job.id);
  const charges = chargesByJobId.get(job.id);
  const invoiceBySide = {
    [BillingLineSide.PAYABLE]: payable,
    [BillingLineSide.RECEIVABLE]: receivable,
  } satisfies Record<BillingLineSide, Invoice | null>;

  if (!charges) {
    return null;
  }

  return (
    <Box
      data-test-id="charges-side-panels"
      sx={{ display: 'grid', gap: 2, gridTemplateColumns: '1fr 1fr' }}
    >
      {BILLING_LINE_SIDES.map((side) => (
        <SidePanel
          key={side}
          charges={charges}
          invoice={invoiceBySide[side]}
          isInvoiceLoading={isInvoicesLoading}
          isPrevailingWage={Boolean(job.order?.prevailingWage)}
          onEdit={() => setEditedSide(side)}
          side={side}
        />
      ))}

      {editedSide && (
        <ChargesInvoiceEditDialog
          jobId={job.id}
          onClose={() => {
            setEditedSide(null);
            void queryClient.invalidateQueries({
              queryKey: jobInvoicesQueryKey(companyId, job.id),
            });
            reload();
          }}
          side={editedSide}
        />
      )}
    </Box>
  );
}

interface SidePanelProps {
  charges: JobCharges;
  invoice: Invoice | null;
  isInvoiceLoading: boolean;
  isPrevailingWage: boolean;
  onEdit: () => void;
  side: BillingLineSide;
}

function SidePanel({
  charges,
  invoice,
  isInvoiceLoading,
  isPrevailingWage,
  onEdit,
  side,
}: SidePanelProps) {
  const { currencyFormatter } = useCompanyCurrency();
  const sideCharges = charges[side];
  const { hasLines, perLoadFreight, perLoadMaterial, settlement } = sideCharges;
  const jobLineRows = charges.jobLines.filter((row) => row.side === side);
  const addOnRows = charges.addOns.filter((row) => row.side === side);
  const parties = getBillingLinesParties(getLinesOfSide(charges.lines, side));
  const hasSingleParty = parties.length === 1;
  const showsPrevailingWageBadge = isPrevailingWage && side === BillingLineSide.PAYABLE;
  const isBilledPerLoad = isSideBilledPerLoad(charges, side);
  const rateCardName = invoice ? getInvoiceRateCardName(invoice) : null;
  const loadsRowSummary = isBilledPerLoad ? perLoadFreight : null;

  const renderChargeRowLine = (
    row: ChargeRow,
    quantity: number | string | null,
    unitStyle: 'full' | 'short',
  ) => (
    <SidePanelLine
      key={row.key}
      item={row.name}
      quantity={quantity}
      rate={
        row.isRejected
          ? NO_VALUE_PLACEHOLDER
          : formatPanelRate(row, currencyFormatter, unitStyle)
      }
      rejection={row}
      total={currencyFormatter(row.total)}
      wasRate={
        row.wasRate === null
          ? null
          : formatPanelRate({ ...row, rate: row.wasRate }, currencyFormatter, unitStyle)
      }
      wasTotal={
        !row.isRejected && row.wasTotalAmount !== null
          ? currencyFormatter(row.wasTotalAmount)
          : null
      }
    />
  );

  return (
    <Box
      data-test-id={`charges-side-panel-${side}`}
      sx={(theme) => ({
        ...chargesRaisedCardSx(theme),
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      })}
    >
      <Box
        sx={(theme) => ({
          alignItems: 'center',
          borderBottom: `1px solid ${theme.brandV2.colors.treadGray7}`,
          display: 'flex',
          gap: 1,
          px: 1.5,
          py: 1,
        })}
      >
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box sx={{ alignItems: 'center', display: 'flex', gap: 0.75 }}>
            <Box
              data-test-id={`charges-side-panel-dot-${side}`}
              sx={(theme) => ({
                bgcolor: sideDotColor[side](theme),
                borderRadius: '50%',
                flexShrink: 0,
                height: theme.spacing(1),
                width: theme.spacing(1),
              })}
            />
            <Typography sx={{ fontSize: '14px', fontWeight: 600 }}>
              {t(sideLabelKeys[side])}
            </Typography>
          </Box>
          {hasSingleParty && (
            <SidePanelLabeledLine
              label={t('approvals.approve_charges.side_panels.for_label')}
              testId="charges-side-panel-party"
              value={parties[0].name ?? NO_VALUE_PLACEHOLDER}
            />
          )}
          {hasLines &&
            (isInvoiceLoading ? (
              <Skeleton sx={{ width: 160 }} variant="text" />
            ) : (
              <Box sx={{ alignItems: 'center', display: 'flex', gap: 1 }}>
                <SidePanelLabeledLine
                  label={t('approvals.approve_charges.side_panels.rate_card_label')}
                  testId="charges-side-panel-rate-card"
                  value={rateCardName ?? t('common.none')}
                />
                {showsPrevailingWageBadge && <PrevailingWageBadge compact />}
              </Box>
            ))}
        </Box>
        {hasLines && (
          <SettlementNumberChip
            hasUnrejectedLines={settlement?.isRejected === false}
            hasUnsettledLines={settlement?.hasUnsettledLines}
            isApproved={settlement?.isSettlementApproved}
            settlementNumbers={settlement?.settlementNumbers ?? []}
            side={side}
          />
        )}
        {hasLines && !isBilledPerLoad && (
          <SmallButton
            onClick={onEdit}
            startIcon={<EditOutlined />}
            sx={{ borderColor: 'divider', color: 'text.primary' }}
            variant="outlined"
          >
            {t('actions.edit')}
          </SmallButton>
        )}
      </Box>

      {hasLines && (
        <Box sx={{ display: 'flex', flexDirection: 'column', px: 1.5, py: 1 }}>
          <Box
            sx={(theme) => ({
              ...PANEL_GRID_SX,
              borderBottom: `1px solid ${theme.brandV2.colors.treadGray7}`,
              pb: 0.5,
            })}
          >
            {[
              t('approvals.approve_charges.side_panels.item'),
              t('approvals.approve_charges.columns.qty'),
              t('common.rate'),
              t('common.total'),
            ].map((heading, index) => (
              <Typography
                key={heading}
                sx={(theme) => ({
                  ...chargesNestedHeaderTextSx(theme),
                  textAlign: index === 0 ? 'left' : 'right',
                })}
              >
                {heading}
              </Typography>
            ))}
          </Box>

          {jobLineRows.map((row) => renderChargeRowLine(row, row.quantity, 'short'))}
          {loadsRowSummary && (
            <SidePanelLine
              item={t('common.loads')}
              quantity={loadsRowSummary.quantity}
              rate={formatPanelRate(loadsRowSummary, currencyFormatter)}
              total={currencyFormatter(loadsRowSummary.total)}
            />
          )}
          {perLoadMaterial && (
            <SidePanelLine
              item={t('form_fields.material')}
              quantity={perLoadMaterial.quantity}
              rate={formatPanelRate(perLoadMaterial, currencyFormatter)}
              total={currencyFormatter(perLoadMaterial.total)}
            />
          )}

          <Box
            sx={{
              alignItems: 'center',
              display: 'flex',
              justifyContent: 'space-between',
              minHeight: 32,
              mt: 2,
            }}
          >
            <Typography sx={(theme) => ({ ...secondaryTextSx(theme), fontWeight: 600 })}>
              {t('approvals.approve_charges.columns.add_ons')}
            </Typography>
            {!isBilledPerLoad && (
              <SmallButton
                onClick={onEdit}
                startIcon={<Add />}
                sx={{ color: 'text.primary' }}
                variant="text"
              >
                {t('approvals.approve_charges.add_on_actions.add')}
              </SmallButton>
            )}
          </Box>
          {addOnRows.length > 0 ? (
            addOnRows.map((row) =>
              renderChargeRowLine(row, row.isPercentRate ? null : row.quantity, 'full'),
            )
          ) : (
            <Typography sx={(theme) => ({ ...secondaryTextSx(theme), py: 0.5 })}>
              {t('approvals.approve_charges.side_panels.no_add_ons')}
            </Typography>
          )}
        </Box>
      )}

      {hasLines && (
        <Box
          data-test-id="charges-side-panel-summary"
          sx={(theme) => ({
            bgcolor: theme.brandV2.colors.treadGray9,
            borderTop: `1px solid ${theme.brandV2.colors.treadGray7}`,
            display: 'flex',
            flexDirection: 'column',
            gap: 1,
            mt: 'auto',
            px: 1.5,
            py: 1.5,
          })}
        >
          <SidePanelSummaryLine
            label={t('common.subtotal')}
            value={currencyFormatter(sideCharges.freight?.total ?? 0)}
          />
          <SidePanelSummaryLine
            label={t('approvals.approve_charges.columns.add_ons')}
            value={currencyFormatter(sideCharges.addOnsTotal)}
          />
          {sideCharges.taxTotal > 0 && (
            <SidePanelSummaryLine
              label={t('approvals.driver_pay.settlements.tax')}
              value={currencyFormatter(sideCharges.taxTotal)}
            />
          )}
          <Divider />
          <SidePanelSummaryLine
            isTotal
            label={t('common.total')}
            value={currencyFormatter(getTotalWithTax(sideCharges))}
          />

          {isBilledPerLoad && (
            <Box
              data-test-id="charges-side-panel-billed-per-load"
              sx={{ alignItems: 'center', display: 'flex', gap: 0.75, pt: 0.5 }}
            >
              <InfoOutlined
                sx={(theme) => ({ color: theme.brandV2.colors.treadBlue, fontSize: 16 })}
              />
              <Typography sx={secondaryTextSx}>
                {t('approvals.approve_charges.side_panels.billed_per_load')}
              </Typography>
            </Box>
          )}
        </Box>
      )}
      {!hasLines && (
        <Typography sx={(theme) => ({ ...secondaryTextSx(theme), px: 1.5, py: 1 })}>
          {t(noLinesKeys[side])}
        </Typography>
      )}
    </Box>
  );
}

interface SidePanelLabeledLineProps {
  label: string;
  testId: string;
  value: string;
}

function SidePanelLabeledLine({ label, testId, value }: SidePanelLabeledLineProps) {
  return (
    <OverflowAwareText data-test-id={testId} sx={{ lineHeight: '18px' }} title={value}>
      <Box
        component="span"
        sx={(theme) => ({ color: theme.brandV2.colors.treadGray1, fontWeight: 400 })}
      >
        {label}
      </Box>{' '}
      <Box
        component="span"
        sx={(theme) => ({ color: theme.brandV2.colors.treadBlack, fontWeight: 600 })}
      >
        {value}
      </Box>
    </OverflowAwareText>
  );
}

interface SidePanelLineProps {
  item: string;
  quantity: number | string | null;
  rate: string;
  rejection?: Pick<ChargeRow, 'isRejected' | 'rejectedReason'>;
  total: string;
  wasRate?: string | null;
  wasTotal?: string | null;
}

function SidePanelLine({
  item,
  quantity,
  rate,
  rejection,
  total,
  wasRate = null,
  wasTotal = null,
}: SidePanelLineProps) {
  const isRejected = rejection?.isRejected === true;

  return (
    <Box data-test-id="charges-side-panel-line" sx={{ ...PANEL_GRID_SX, py: 0.5 }}>
      <Box sx={{ alignItems: 'center', display: 'flex', gap: 0.5, minWidth: 0 }}>
        <OverflowAwareText sx={{ fontWeight: 600 }} title={item}>
          {item}
        </OverflowAwareText>
        {isRejected && <RejectedChip reason={rejection?.rejectedReason ?? null} />}
      </Box>
      <OverflowAwareText sx={{ textAlign: 'right' }}>
        {quantity ?? NO_VALUE_PLACEHOLDER}
      </OverflowAwareText>
      <Box sx={{ minWidth: 0, textAlign: 'right' }}>
        <OverflowAwareText title={rate}>{rate}</OverflowAwareText>
        {wasRate !== null && <WasText amount={wasRate} />}
      </Box>
      <Box sx={{ minWidth: 0, textAlign: 'right' }}>
        <OverflowAwareText sx={isRejected ? rejectedAmountSx : undefined}>
          {total}
        </OverflowAwareText>
        {wasTotal !== null && <WasText amount={wasTotal} />}
      </Box>
    </Box>
  );
}

interface SidePanelSummaryLineProps {
  isTotal?: boolean;
  label: string;
  value: string;
}

function SidePanelSummaryLine({
  isTotal = false,
  label,
  value,
}: SidePanelSummaryLineProps) {
  return (
    <Box
      sx={{ alignItems: 'baseline', display: 'flex', justifyContent: 'space-between' }}
    >
      <Typography sx={{ fontSize: isTotal ? '14px' : '12px', fontWeight: 600 }}>
        {label}
      </Typography>
      <Typography
        sx={{ fontSize: isTotal ? '14px' : '12px', fontWeight: isTotal ? 700 : 600 }}
      >
        {value}
      </Typography>
    </Box>
  );
}
